import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Chip,
    Alert
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { Assessment, Verified } from '@mui/icons-material';

const SystemPerformanceChart = ({ systemHealth = {}, loading = false }) => {
    // Use PURE Spring Actuator data with proper validation
    const generateActuatorPerformanceData = () => {
        // Ensure we have valid Spring Actuator data
        const hasValidData = systemHealth && Object.keys(systemHealth).length > 0;

        // Direct Spring Actuator metrics only with fallback values
        const metrics = [
            {
                metric: 'CPU Usage',
                value: hasValidData ? (systemHealth.cpuUsage || 0) : 0,
                color: '#2196f3',
                status: (systemHealth.cpuUsage || 0) < 70 ? 'excellent' : (systemHealth.cpuUsage || 0) < 85 ? 'good' : 'warning',
                source: 'Spring Actuator'
            },
            {
                metric: 'Memory Usage',
                value: hasValidData ? (systemHealth.memoryUsage || 0) : 0,
                color: '#4caf50',
                status: (systemHealth.memoryUsage || 0) < 80 ? 'excellent' : (systemHealth.memoryUsage || 0) < 90 ? 'good' : 'warning',
                source: 'Spring Actuator'
            },
            {
                metric: 'System Uptime',
                value: hasValidData ? (systemHealth.uptime || 0) : 0,
                color: '#ff9800',
                status: (systemHealth.uptime || 0) > 99 ? 'excellent' : (systemHealth.uptime || 0) > 95 ? 'good' : 'normal',
                source: 'Spring Actuator'
            },
            {
                metric: 'Database Health',
                value: hasValidData ? (systemHealth.databaseHealth || 0) : 0,
                color: '#9c27b0',
                status: (systemHealth.databaseHealth || 0) > 95 ? 'excellent' : (systemHealth.databaseHealth || 0) > 85 ? 'good' : 'normal',
                source: 'Spring Actuator'
            },
            {
                metric: 'Response Time Score',
                value: hasValidData && systemHealth.responseTime ? Math.max(0, 100 - (systemHealth.responseTime * 25)) : 0,
                color: '#607d8b',
                status: (systemHealth.responseTime || 10) < 1 ? 'excellent' : (systemHealth.responseTime || 10) < 2 ? 'good' : 'normal',
                source: 'Calculated from Actuator',
                rawValue: `${systemHealth.responseTime || 0}s`
            }
        ];

        return metrics;
    };

    const performanceData = generateActuatorPerformanceData();
    const hasRealData = systemHealth && Object.keys(systemHealth).length > 0;

    const getStatusColor = (status) => {
        switch (status) {
            case 'excellent': return '#4caf50';
            case 'good': return '#8bc34a';
            case 'normal': return '#ff9800';
            case 'warning': return '#ff5722';
            default: return '#757575';
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Box sx={{
                    background: 'white',
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    boxShadow: 2,
                    minWidth: 200
                }}>
                    <Typography variant="body2" fontWeight={600}>
                        {label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: data.color }}>
                        Value: {data.value ? data.value.toFixed(1) : 0}%
                        {data.rawValue && ` (${data.rawValue})`}
                    </Typography>
                    <Chip
                        label={data.status}
                        size="small"
                        sx={{
                            mt: 0.5,
                            backgroundColor: `${getStatusColor(data.status)}20`,
                            color: getStatusColor(data.status),
                            fontSize: '0.7rem'
                        }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        Source: {data.source}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>Loading Spring Actuator metrics...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assessment sx={{ color: '#9c27b0', mr: 1, fontSize: 28 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                            Spring Boot Actuator Metrics
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Real-time system performance from Spring Actuator endpoints
                        </Typography>
                    </Box>
                    <Chip
                        icon={<Verified />}
                        label={hasRealData ? "LIVE ACTUATOR" : "WAITING..."}
                        size="small"
                        sx={{
                            background: hasRealData ? 'linear-gradient(45deg, #4caf50, #66bb6a)' : 'linear-gradient(45deg, #ff9800, #ffb74d)',
                            color: 'white',
                            fontWeight: 600
                        }}
                    />
                </Box>

                {!hasRealData && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                            🔄 Waiting for Spring Actuator data. If bars are not showing, please check:
                            <br />• Spring Actuator endpoints are enabled
                            <br />• System health API is responding
                            <br />• Backend service is running
                        </Typography>
                    </Alert>
                )}

                <ResponsiveContainer width="100%" height={hasRealData ? 280 : 200}>
                    <BarChart data={performanceData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="metric"
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            domain={[0, 100]}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                            dataKey="value"
                            radius={[4, 4, 0, 0]}
                            stroke="#fff"
                            strokeWidth={1}
                        >
                            {performanceData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default SystemPerformanceChart;
